import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useUsersManagementApi } from "src/_common/hooks/actions";
import { useInterviewApi } from "src/_common/hooks/actions/interview/appInterviewApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useHistory } from "react-router-dom";
import { URLS } from "src/_config";
import moment from "moment";

interface Member {
  id: number;
  name: string;
  email: string;
  designation: string;
  profile_photo_url?: string;
  profile_image_url?: string;
  status: number;
  user_type: string;
  company_id: number;
}

interface BulkScheduleInterviewFormData {
  selectedMember: string;
}

interface TimeSlot {
  datetime_start: string;
  datetime_end: string;
  formatted_time?: string;
}

interface DateGroup {
  date: string;
  slots: TimeSlot[];
}

interface Candidate {
  id: number;
  name: string;
  screeningId?: string;
}

interface BulkScheduleInterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidates: Candidate[];
  jobId: string;
  onSchedule: (data: any) => void;
}

const BulkScheduleInterviewModal: React.FC<BulkScheduleInterviewModalProps> = ({
  isOpen,
  onClose,
  candidates,
  jobId,
  onSchedule,
}) => {
  const { t } = useTranslation("job-details");
  const history = useHistory();
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);

  const { callGetUsers } = useUsersManagementApi();
  const { callGetAvailableTimeSlots } = useInterviewApi();

  // State for available time slots grouped by date
  const [availableTimeSlots, setAvailableTimeSlots] = useState<any[]>([]);
  const [groupedSlots, setGroupedSlots] = useState<DateGroup[]>([]);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);

  // State for selected time slots
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<string[]>([]); // Array of datetime strings

  // State for accordion (which date is expanded)
  const [expandedDate, setExpandedDate] = useState<string | null>(null);

  const toast = useToaster();

  // Validation schema
  const scheduleSchema = yup.object().shape({
    selectedMember: yup.string().required(t("jobDetails.interview.scheduleModal.validation.selectMember")),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<BulkScheduleInterviewFormData>({
    resolver: yupResolver(scheduleSchema),
    defaultValues: {
      selectedMember: "",
    },
  });

  // Watch selected member for UI updates
  const selectedMember = watch("selectedMember");

  // Group slots by date
  const groupSlotsByDate = (slots: any[]): DateGroup[] => {
    const grouped: { [key: string]: TimeSlot[] } = {};

    slots.forEach((slot) => {
      const date = moment(slot.datetime_start, "YYYY-MM-DD HH:mm:ss").format("YYYY-MM-DD");
      const formattedTime = moment(slot.datetime_start, "YYYY-MM-DD HH:mm:ss").format("HH:mm");

      if (!grouped[date]) {
        grouped[date] = [];
      }

      grouped[date].push({
        datetime_start: slot.datetime_start,
        datetime_end: slot.datetime_end,
        formatted_time: formattedTime,
      });
    });

    // Convert to array and sort by date
    return Object.keys(grouped)
      .sort()
      .map((date) => ({
        date,
        slots: grouped[date].sort((a, b) => moment(a.datetime_start).valueOf() - moment(b.datetime_start).valueOf()),
      }));
  };

  // Fetch available time slots when member changes
  const getAvailableSlots = (selectedMember: any) => {
    setIsLoadingTimeSlots(true);
    // Clear selected slots when member changes
    setSelectedTimeSlots([]);
    setExpandedDate(null);

    callGetAvailableTimeSlots(
      { member_id: selectedMember },
      (message: string, data: any) => {
        setAvailableTimeSlots(data?.all_slots || []);
        const grouped = groupSlotsByDate(data?.all_slots || []);
        setGroupedSlots(grouped);

        // Auto-expand first date if available
        if (grouped.length > 0) {
          setExpandedDate(grouped[0].date);
        }

        setIsLoadingTimeSlots(false);
      },
      (error: string) => {
        setAvailableTimeSlots([]);
        setGroupedSlots([]);
        setIsLoadingTimeSlots(false);
      },
    );
  };

  // Toggle time slot selection
  const toggleTimeSlot = (datetimeStart: string) => {
    setSelectedTimeSlots((prev) => {
      if (prev.includes(datetimeStart)) {
        return prev.filter((slot) => slot !== datetimeStart);
      } else {
        return [...prev, datetimeStart];
      }
    });
  };

  // Toggle date checkbox - select/deselect all times for that date
  const toggleDateSelection = (date: string) => {
    const dateSlots = groupedSlots.find((g) => g.date === date);
    if (!dateSlots) return;

    const dateTimes = dateSlots.slots.map((s) => s.datetime_start);
    const allSelected = dateTimes.every((dt) => selectedTimeSlots.includes(dt));

    if (allSelected) {
      // Deselect all times for this date
      setSelectedTimeSlots((prev) => prev.filter((slot) => !dateTimes.includes(slot)));
    } else {
      // Select all times for this date
      setSelectedTimeSlots((prev) => {
        const newSlots = [...prev];
        dateTimes.forEach((dt) => {
          if (!newSlots.includes(dt)) {
            newSlots.push(dt);
          }
        });
        return newSlots;
      });
    }
  };

  // Check if date has any selected times
  const isDatePartiallySelected = (date: string) => {
    const dateSlots = groupedSlots.find((g) => g.date === date);
    if (!dateSlots) return false;

    const dateTimes = dateSlots.slots.map((s) => s.datetime_start);
    return dateTimes.some((dt) => selectedTimeSlots.includes(dt));
  };

  // Check if all times for a date are selected
  const isDateFullySelected = (date: string) => {
    const dateSlots = groupedSlots.find((g) => g.date === date);
    if (!dateSlots) return false;

    const dateTimes = dateSlots.slots.map((s) => s.datetime_start);
    return dateTimes.length > 0 && dateTimes.every((dt) => selectedTimeSlots.includes(dt));
  };

  // Toggle accordion
  const toggleAccordion = (date: string) => {
    setExpandedDate((prev) => (prev === date ? null : date));
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Load team members when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchMembers();
      // Reset form when modal opens
      reset();
      setSelectedTimeSlots([]);
      setExpandedDate(null);
    }
  }, [isOpen, reset]);

  const fetchMembers = () => {
    setIsLoadingMembers(true);
    const params = {
      limit: 50, // Get more members for selection
      offset: 0,
    };

    callGetUsers(
      params,
      (message: string, data: any) => {
        console.log("Members loaded successfully:", data);
        if (data && data.members) {
          setMembers(data.members);
        }
        setIsLoadingMembers(false);
      },
      (error: string) => {
        console.error("Error loading members:", error);
        toast.error("Failed to load team members: " + error);
        setIsLoadingMembers(false);
      },
    );
  };

  const onSubmit = async (data: BulkScheduleInterviewFormData) => {
    try {
      // Validate that we have enough time slots for all candidates
      if (selectedTimeSlots.length < candidates.length) {
        toast.error(
          `Please select at least ${candidates.length} time slot${candidates.length > 1 ? "s" : ""} for ${candidates.length} candidate${candidates.length > 1 ? "s" : ""}. Currently selected: ${selectedTimeSlots.length}`,
        );
        return;
      }

      console.log("Bulk scheduling interview with data:", data);

      // Group selected time slots by date
      const groupedTimeSlots: { [key: string]: string[] } = {};

      selectedTimeSlots.forEach((slot) => {
        const date = moment(slot, "YYYY-MM-DD HH:mm:ss").format("YYYY-MM-DD");
        const time = moment(slot, "YYYY-MM-DD HH:mm:ss").format("HH:mm");

        if (!groupedTimeSlots[date]) {
          groupedTimeSlots[date] = [];
        }

        groupedTimeSlots[date].push(time);
      });

      // Format member_available_time
      const memberAvailableTime = Object.keys(groupedTimeSlots)
        .sort()
        .map((date) => ({
          date: date,
          time_slot: groupedTimeSlots[date].sort(),
        }));

      // Pass schedule data to parent — parent handles all API calls in correct order
      onSchedule({
        ...data,
        member_id: data.selectedMember,
        memberAvailableTime,
      });

      // Close modal immediately — parent manages the API chain
      handleClose();
    } catch (error) {
      console.error("Error bulk scheduling interviews:", error);
      toast.error(t("jobDetails.interview.scheduleModal.scheduleError"));
    }
  };

  const handleClose = () => {
    reset();
    setSelectedTimeSlots([]);
    setExpandedDate(null);
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleAddAvailability = () => {
    // Store the selected member ID in localStorage
    if (selectedMember) {
      localStorage.setItem("selectedMemberForAvailability", selectedMember);
      // Navigate to interview schedule page
      history.push(URLS.INTERVIEW.AVALIBILITY);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="currency-modal-overlay" onClick={handleOverlayClick}>
      <div className="currency-modal schedule-interview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="currency-modal-header">
          <h3 className="currency-head">
            {candidates.length === 1
              ? `Schedule Interview for ${candidates[0].name}`
              : `${t("jobDetails.screening.bulkScheduleTitle")} (${candidates.length} ${t("jobDetails.screening.candidates")})`}
          </h3>
          <button className="close-modal" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="ai-description-content">
          {/* Display list of selected candidates - only show if more than 1 candidate */}
          {candidates.length > 1 && (
            <div className="bulk-candidates-list" style={{ marginBottom: "20px" }}>
              <label className="form-label">{t("jobDetails.screening.selectedCandidates")}:</label>
              <div
                style={{
                  maxHeight: "150px",
                  overflowY: "auto",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "12px",
                  backgroundColor: "#f9fafb",
                }}
              >
                {candidates.map((candidate, index) => (
                  <div
                    key={candidate.id}
                    style={{
                      padding: "6px 0",
                      borderBottom: index < candidates.length - 1 ? "1px solid #e5e7eb" : "none",
                    }}
                  >
                    <span style={{ fontSize: "14px", color: "#374151" }}>
                      {index + 1}. {candidate.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Team Member Selection */}
            <div className="drjob-form-group">
              <label className="form-label">
                {t("jobDetails.interview.scheduleModal.selectMember")} <span className="mandatory-mark">*</span>
              </label>
              <Controller
                name="selectedMember"
                control={control}
                render={({ onChange, onBlur, value }) => (
                  <select
                    className={`form-control ${errors.selectedMember ? "is-invalid" : ""}`}
                    value={value || ""}
                    onChange={(e) => {
                      e.preventDefault();
                      onChange(e.target.value);
                      getAvailableSlots(e.target.value);
                    }}
                    onBlur={onBlur}
                    disabled={isLoadingMembers}
                  >
                    <option value="">
                      {isLoadingMembers
                        ? t("jobDetails.interview.scheduleModal.loadingMembers")
                        : t("jobDetails.interview.scheduleModal.selectPlaceholder")}
                    </option>

                    {/* Add all members */}
                    {members
                      .filter((member) => member.name && member.name.trim())
                      .map((member) => (
                        <option key={member.id} value={member.id.toString()}>
                          {member.designation ? `${member.name} - ${member.designation}` : member.name}
                        </option>
                      ))}
                  </select>
                )}
              />
              {errors.selectedMember && <span className="error-message">{errors.selectedMember.message}</span>}
            </div>

            {/* Interview Date and Time - Accordion */}
            <div className="drjob-form-group">
              <label className="form-label">
                {t("jobDetails.interview.scheduleModal.interviewDateTime")} <span className="mandatory-mark">*</span>
              </label>

              {selectedMember && isLoadingTimeSlots && (
                <div style={{ padding: "20px", textAlign: "center" }}>
                  <span>Loading available time slots...</span>
                </div>
              )}

              {selectedMember && !isLoadingTimeSlots && groupedSlots.length === 0 && (
                <div className="invalid-feedback" style={{ display: "block", marginTop: "10px" }}>
                  <span>No available slots for selected member. </span>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleAddAvailability();
                    }}
                    style={{
                      color: "#3b82f6",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontWeight: "500",
                    }}
                  >
                    Add Availability
                  </a>
                </div>
              )}

              {selectedMember && !isLoadingTimeSlots && groupedSlots.length > 0 && (
                <div style={{ marginTop: "10px" }}>
                  {/* Status message */}
                  {selectedTimeSlots.length < candidates.length && (
                    <div
                      style={{ marginBottom: "15px", padding: "10px", backgroundColor: "#f3f4f6", borderRadius: "6px" }}
                    >
                      <span style={{ fontSize: "14px", color: "#374151" }}>
                        <span style={{ color: "#ef4444" }}>
                          Please select at least {candidates.length} time slot{candidates.length > 1 ? "s" : ""}
                        </span>
                      </span>
                    </div>
                  )}

                  {/* Accordion */}
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      maxHeight: "300px",
                      overflowY: "auto",
                    }}
                  >
                    {groupedSlots.map((dateGroup, index) => {
                      const isExpanded = expandedDate === dateGroup.date;
                      const isPartiallySelected = isDatePartiallySelected(dateGroup.date);
                      const isFullySelected = isDateFullySelected(dateGroup.date);
                      const selectedCount = dateGroup.slots.filter((slot) =>
                        selectedTimeSlots.includes(slot.datetime_start),
                      ).length;

                      return (
                        <div
                          key={dateGroup.date}
                          style={{
                            borderBottom: index < groupedSlots.length - 1 ? "1px solid #e5e7eb" : "none",
                          }}
                        >
                          {/* Date Header */}
                          <div
                            style={{
                              padding: "12px 16px",
                              backgroundColor: isExpanded ? "#f9fafb" : "#ffffff",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <div
                              style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}
                              onClick={() => toggleAccordion(dateGroup.date)}
                            >
                              {/* Checkbox for date */}
                              <input
                                type="checkbox"
                                checked={isFullySelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleDateSelection(dateGroup.date);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  width: "18px",
                                  height: "18px",
                                  cursor: "pointer",
                                  accentColor: "#3b82f6",
                                }}
                                ref={(input) => {
                                  if (input) {
                                    input.indeterminate = isPartiallySelected && !isFullySelected;
                                  }
                                }}
                              />

                              {/* Date text */}
                              <span style={{ fontSize: "15px", fontWeight: "500", color: "#111827" }}>
                                {moment(dateGroup.date).format("dddd, MMMM D, YYYY")}
                              </span>

                              {/* Selected count badge */}
                              {selectedCount > 0 && (
                                <span
                                  style={{
                                    fontSize: "12px",
                                    backgroundColor: "#a5c5e5",
                                    color: "#1a1a1a",
                                    padding: "2px 8px",
                                    borderRadius: "12px",
                                    fontWeight: "500",
                                  }}
                                >
                                  {selectedCount}
                                </span>
                              )}
                            </div>

                            {/* Expand/Collapse icon */}
                            <i
                              onClick={() => toggleAccordion(dateGroup.date)}
                              className={`fa ${isExpanded ? "fa-chevron-up" : "fa-chevron-down"}`}
                              style={{
                                fontSize: "16px",
                                color: "#6b7280",
                                cursor: "pointer",
                              }}
                            ></i>
                          </div>

                          {/* Time slots */}
                          {isExpanded && (
                            <div
                              style={{
                                padding: "12px 16px",
                                backgroundColor: "#f9fafb",
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                                gap: "10px",
                              }}
                            >
                              {dateGroup.slots.map((slot) => {
                                const isSelected = selectedTimeSlots.includes(slot.datetime_start);

                                return (
                                  <label
                                    key={slot.datetime_start}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      padding: "8px 12px",
                                      backgroundColor: isSelected ? "#e8f1f8" : "#ffffff",
                                      border: isSelected ? "2px solid #a5c5e5" : "1px solid #e5e7eb",
                                      borderRadius: "6px",
                                      cursor: "pointer",
                                      transition: "all 0.2s",
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isSelected) {
                                        e.currentTarget.style.backgroundColor = "#f3f4f6";
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isSelected) {
                                        e.currentTarget.style.backgroundColor = "#ffffff";
                                      }
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => toggleTimeSlot(slot.datetime_start)}
                                      style={{
                                        width: "16px",
                                        height: "16px",
                                        cursor: "pointer",
                                        accentColor: "#3b82f6",
                                      }}
                                    />
                                    <span
                                      style={{
                                        fontSize: "14px",
                                        color: "#374151",
                                        // color: isSelected ? "#5a8db8" : "#374151",
                                        fontWeight: isSelected ? "500" : "400",
                                      }}
                                    >
                                      {slot.formatted_time}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!selectedMember && (
                <div className="invalid-feedback" style={{ display: "block", marginTop: "10px" }}>
                  <span>Please select a team member first</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="modal-actions">
              <button type="button" className="btn-cancel" onClick={handleClose}>
                {t("jobDetails.interview.scheduleModal.cancel")}
              </button>
              <button
                type="submit"
                className="btn-preview"
                disabled={selectedTimeSlots.length < candidates.length}
              >
                {t("jobDetails.screening.bulkScheduleButton")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BulkScheduleInterviewModal;
